/* eslint-disable react/prop-types */
/**
 * Phase 19 — "fraction of inbound threats defeated" Wilson-CI
 * surface plot (WS-E result panel, shipped in CP4 for the preview).
 *
 * Pure inline SVG — matches the 18.05 ``QTableHeatmap.jsx`` precedent.
 * No new chart libraries (Plotly is in the stack but deliberately
 * not used per CP4's brief: "reuse the existing custom SVG pattern").
 *
 * Renders a confidence band (Wilson lower/upper) as a tinted area,
 * the point-estimate as a horizontal line, and each per-sample point
 * (one per Monte-Carlo sample) as a small marker. Tabular numerals
 * throughout; defence palette; UK spelling.
 */
import { useMemo, useState } from "react";
import { DEFENCE_PALETTE } from "@/components/phase19/placementUtils";

function clamp01(x) {
    return Math.max(0, Math.min(1, Number.isFinite(x) ? x : 0));
}

export default function FractionDefeatedWilsonSurface({
    fractionDefeated,
    sampleSurface = [],
    width = 460,
    height = 170,
}) {
    const [hover, setHover] = useState(null);
    const point = clamp01(fractionDefeated?.point_estimate);
    const ci = fractionDefeated?.wilson_ci || {};
    const lower = clamp01(ci.lower);
    const upper = clamp01(ci.upper);

    const ML = 36;  // left margin for y-axis labels
    const MR = 10;
    const MT = 12;
    const MB = 22;
    const plotW = width - ML - MR;
    const plotH = height - MT - MB;
    const yToPx = (y) => MT + (1 - clamp01(y)) * plotH;

    const samples = useMemo(
        () =>
            sampleSurface.map((s, i) => ({
                idx: i,
                value: clamp01(s.point_estimate ?? s.fraction_defeated),
            })),
        [sampleSurface],
    );
    const xToPx = (i) =>
        ML + (samples.length <= 1 ? plotW / 2 : (i / (samples.length - 1)) * plotW);

    const yTicks = [0, 0.25, 0.5, 0.75, 1.0];

    return (
        <figure
            className="flex flex-col gap-1"
            data-testid="fraction-defeated-wilson-surface"
        >
            <figcaption
                className="flex items-baseline justify-between text-[11px] uppercase tracking-wide"
                style={{ color: DEFENCE_PALETTE.navy }}
            >
                <span>Fraction of inbound threats defeated</span>
                <span
                    style={{
                        color: DEFENCE_PALETTE.steelGray,
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    {(point * 100).toFixed(1)}%
                    {" ± "}
                    {(((upper - lower) / 2) * 100).toFixed(1)}
                </span>
            </figcaption>
            <svg
                width={width}
                height={height}
                viewBox={`0 0 ${width} ${height}`}
                style={{
                    fontVariantNumeric: "tabular-nums",
                    background: "#FFFFFF",
                    border: `1px solid ${DEFENCE_PALETTE.steelGray}`,
                    borderRadius: 4,
                }}
            >
                {/* y-axis gridlines + labels */}
                {yTicks.map((y) => (
                    <g key={y}>
                        <line
                            x1={ML}
                            x2={width - MR}
                            y1={yToPx(y)}
                            y2={yToPx(y)}
                            stroke="#E5EAF0"
                            strokeWidth={1}
                        />
                        <text
                            x={ML - 6}
                            y={yToPx(y) + 3}
                            fontSize={9}
                            textAnchor="end"
                            fill={DEFENCE_PALETTE.steelGray}
                        >
                            {(y * 100).toFixed(0)}%
                        </text>
                    </g>
                ))}
                {/* CI band */}
                <rect
                    x={ML}
                    y={yToPx(upper)}
                    width={plotW}
                    height={Math.max(1, yToPx(lower) - yToPx(upper))}
                    fill={DEFENCE_PALETTE.teal}
                    opacity={0.18}
                    data-testid="wilson-ci-band"
                />
                {/* Lower / upper guides */}
                <line
                    x1={ML}
                    x2={width - MR}
                    y1={yToPx(lower)}
                    y2={yToPx(lower)}
                    stroke={DEFENCE_PALETTE.teal}
                    strokeWidth={1.2}
                    strokeDasharray="3 3"
                />
                <line
                    x1={ML}
                    x2={width - MR}
                    y1={yToPx(upper)}
                    y2={yToPx(upper)}
                    stroke={DEFENCE_PALETTE.teal}
                    strokeWidth={1.2}
                    strokeDasharray="3 3"
                />
                {/* Point estimate */}
                <line
                    x1={ML}
                    x2={width - MR}
                    y1={yToPx(point)}
                    y2={yToPx(point)}
                    stroke={DEFENCE_PALETTE.navy}
                    strokeWidth={2}
                    data-testid="wilson-point-estimate"
                />
                {/* Per-sample markers */}
                {samples.map((s) => (
                    <circle
                        key={s.idx}
                        cx={xToPx(s.idx)}
                        cy={yToPx(s.value)}
                        r={2.5}
                        fill={DEFENCE_PALETTE.navy}
                        opacity={0.7}
                        onMouseEnter={() => setHover(s)}
                        onMouseLeave={() => setHover(null)}
                        data-testid={`sample-marker-${s.idx}`}
                    />
                ))}
                {/* x-axis label */}
                <text
                    x={(ML + width - MR) / 2}
                    y={height - 4}
                    fontSize={9}
                    textAnchor="middle"
                    fill={DEFENCE_PALETTE.steelGray}
                >
                    Monte-Carlo sample index ({samples.length})
                </text>
            </svg>
            {hover && (
                <p
                    className="text-[10px]"
                    style={{
                        color: DEFENCE_PALETTE.steelGray,
                        fontVariantNumeric: "tabular-nums",
                    }}
                >
                    sample {hover.idx} · {(hover.value * 100).toFixed(1)}%
                </p>
            )}
        </figure>
    );
}
